import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Spinner } from 'react-bootstrap';
import { FaRegTrashAlt, FaSearch } from 'react-icons/fa';
import { itemsOnPage } from '../../utils/const';
import { usePermission } from '../../hooks';
import HaventPermission from '../../assets/HaventPermission';
import usePageWidth from '../shared/usePageWidth';
import CustomSelect from '../CustomSelect';
import DocumentCard, { formatDate } from './DocumentCard';
import styles from './styles.module.css';

// «Все» в CustomSelect — пустая строка; такой параметр в запрос не уходит вовсе
// (ноль вместо «Все» бэк понимает как идентификатор справочника, см. actions/document.js)
const ALL = '';

// в ключе версия: сохранения прежнего вида пришли бы с пустым периодом
// и перебили бы значения по умолчанию
const STORAGE_KEY = 'eduDocuments.v2';

// длина текстового отбора ограничена: строка уходит в ILIKE и в адрес запроса,
// на очень длинном значении перечень падал
const SEARCH_MAXLENGTH = { fio: 100, number: 50 };

const isoDate = value => value.format('YYYY-MM-DD');

const initialFilters = {
    cat: ALL,
    type: ALL,
    year: ALL,
    // период по умолчанию — год назад … сегодня
    from: isoDate(moment().subtract(1, 'year')),
    to: isoDate(moment()),
    fio: '',
    number: '',
};

const trimToLimit = (name, value) => String(value ?? '').slice(0, SEARCH_MAXLENGTH[name]);

// перечни приходят разными методами — идентификатор и наименование у каждого свои
const optionsOf = (list, valueKey, labelKey) => {
    const seen = new Set();
    return (Array.isArray(list) ? list : [])
        .map(item => ({ value: item[valueKey], name: item[labelKey] || '' }))
        .filter(({ value }) => {
            if (value == null || seen.has(value)) return false;
            seen.add(value);
            return true;
        });
};

// многоточие ставится только там, где пропущено больше одной страницы;
// стрелки ‹ › рисуются отдельно, по краям числового ряда
const pagerItems = (page, pages) => {
    if (pages <= 1) return [];
    const keep = new Set([1, pages, page - 1, page, page + 1]);
    if (page <= 4) [2, 3, 4, 5].forEach(i => keep.add(i));
    if (page >= pages - 3) [pages - 1, pages - 2, pages - 3, pages - 4].forEach(i => keep.add(i));
    const list = [...keep].filter(i => i >= 1 && i <= pages).sort((a, b) => a - b);

    const items = [];
    list.forEach((value, i) => {
        if (i) {
            const gap = value - list[i - 1];
            if (gap === 2) items.push(list[i - 1] + 1);
            else if (gap > 2) items.push('gap');
        }
        items.push(value);
    });
    return items;
};

// Колонки перечня. Ключи строки приходят от бэка по-русски и совпадают
// с заголовками; `kind` задаёт правило сравнения при сортировке.
const COLUMNS = [
    { key: 'Категория', title: 'Категория', kind: 'text', col: 'colCat' },
    { key: 'ФИО', title: 'ФИО', kind: 'text', cell: 'strong' },
    { key: 'Регистр. номер', title: 'Регистрационный номер', kind: 'text', col: 'colReg', cell: 'mono' },
    { key: 'Вид документа', title: 'Вид документа', kind: 'text', col: 'colVid' },
    { key: 'Дата выдачи', title: 'Дата выдачи', kind: 'date', col: 'colDt', cell: 'mono' },
    { key: 'Серия', title: 'Серия', kind: 'text', col: 'colSer', cell: 'mono' },
    { key: 'Номер', title: 'Номер', kind: 'text', col: 'colNum', cell: 'mono' },
    { key: 'Дата принятия решения ГАК', title: 'Дата принятия решения ГАК', kind: 'date', col: 'colDt', cell: 'mono' },
    { key: 'Примечание', title: 'Примечание', kind: 'text', cell: 'left', clamp: true },
    { key: 'ООП', title: 'ООП', kind: 'text', col: 'colOop' },
];

const compareValues = (a, b, kind) => {
    if (kind === 'date') return moment.utc(a || 0).valueOf() - moment.utc(b || 0).valueOf();
    return String(a == null ? '' : a).localeCompare(String(b == null ? '' : b), 'ru', { numeric: true });
};

const DocumentList = ({
    list,
    totalCount,
    pagesCount,
    isLoading,
    errorStatus,

    docTypes,
    counerType,
    yearsType,

    filesList,
    filesIsLoading,
    filesErrorStatus,

    getDocsList,
    getDocTypes,
    getCownerTypes,
    getYearsTypes,
    getFilesList,
    clearDocuments,
}) => {

    usePageWidth('pageWide');

    const { readRight } = usePermission(
        'Режимы',
        'Документы об образовании. Книга регистрации выданных документов об образовании'
    );

    // отбор из прошлого сеанса — плоские строки, без сериализованных moment
    const restored = useRef((() => {
        try {
            const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
            if (!saved) return initialFilters;
            // сохранение могло быть сделано до ограничения длины
            return {
                ...initialFilters,
                ...saved,
                fio: trimToLimit('fio', saved.fio),
                number: trimToLimit('number', saved.number),
            };
        } catch {
            return initialFilters;
        }
    })());

    // filters — то, что в полях; applied — то, что ушло в запрос
    // (текстовые поля попадают в applied с задержкой)
    const [filters, setFilters] = useState(restored.current);
    const [applied, setApplied] = useState(restored.current);
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState({ key: 'Дата выдачи', dir: 1 });
    const [current, setCurrent] = useState(null);

    const catOptions = useMemo(() => optionsOf(counerType, 'oid', 'owner_kind'), [counerType]);
    const typeOptions = useMemo(() => optionsOf(docTypes, 'did', 'docname'), [docTypes]);
    const yearOptions = useMemo(() => optionsOf(yearsType, 'xp_key', 'name'), [yearsType]);

    useEffect(() => {
        getCownerTypes();
        getDocTypes();
        getYearsTypes();
        return () => clearDocuments();
    }, []);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    }, [filters]);

    // отбор применяется сразу, отдельной кнопки «Обновить» в режиме нет
    useEffect(() => {
        getDocsList(page, applied);
    }, [applied, page]);

    // ФИО и регистрационный номер уходят в запрос с задержкой, а не на каждый символ
    const firstTextRun = useRef(true);
    useEffect(() => {
        if (firstTextRun.current) {
            firstTextRun.current = false;
            return;
        }
        const timer = setTimeout(() => {
            setApplied(prev => ({ ...prev, fio: filters.fio, number: filters.number }));
            setPage(0);
        }, 400);
        return () => clearTimeout(timer);
    }, [filters.fio, filters.number]);

    const handleChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
        setApplied(prev => ({ ...prev, [name]: value }));
        setPage(0);
    };

    // maxLength режет ввод в самом поле; здесь та же граница на вставку из буфера
    const handleTextChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: trimToLimit(name, value) }));
    };

    // Enter применяет текстовое поле, не дожидаясь задержки
    const handleTextKeyDown = (e) => {
        if (e.key !== 'Enter') return;
        setApplied(prev => ({ ...prev, fio: filters.fio, number: filters.number }));
        setPage(0);
    };

    const isFiltersDirty = Object.keys(initialFilters)
        .some(key => filters[key] !== initialFilters[key]);

    // от чего пусто: от условий пользователя или от периода по умолчанию
    const isSelectionSet = Object.keys(initialFilters)
        .some(key => applied[key] !== initialFilters[key]);

    const handleResetFilters = () => {
        setFilters(initialFilters);
        setApplied(initialFilters);
        setPage(0);
    };

    const handlePageChange = (nextPage) => {
        setPage(nextPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Сортировка локальная, в пределах текущей страницы: при переданном maxResults
    // бэк не сортирует вовсе и порядок строк в странице не определён.
    const handleSort = (key) => {
        setSort(prev => prev.key === key ? { key, dir: -prev.dir } : { key, dir: 1 });
    };

    const rows = useMemo(() => {
        const column = COLUMNS.find(c => c.key === sort.key);
        const source = Array.isArray(list) ? [...list] : [];
        if (!column) return source;
        return source.sort((a, b) => compareValues(a[column.key], b[column.key], column.kind) * sort.dir);
    }, [list, sort]);

    const renderValue = (row, column) => {
        const raw = column.kind === 'date' ? formatDate(row[column.key]) : row[column.key];
        if (!raw && raw !== 0) return <span className={styles.dash}>—</span>;
        return column.clamp ? <div className={styles.clamp}>{raw}</div> : raw;
    };

    const renderSelect = (label, name, options, widthClass) => (
        <div className={widthClass}>
            <CustomSelect
                title={label}
                width="100%"
                options={options}
                value={filters[name] ?? ALL}
                setFieldValue={handleChange}
                valuesName={name}
            />
        </div>
    );

    const renderDate = (label, name) => (
        <div className={styles.fDate}>
            <label className={styles.filterLabel} htmlFor={`edudoc-${name}`}>{label}</label>
            <input
                id={`edudoc-${name}`}
                className={styles.dateInput}
                type="date"
                value={filters[name]}
                onChange={e => handleChange(name, e.target.value)}
            />
        </div>
    );

    const renderSearch = (label, name, placeholder) => (
        <div className={styles.srchWrap}>
            <label className={styles.filterLabel} htmlFor={`edudoc-${name}`}>{label}</label>
            <FaSearch className={styles.srchIcon} size={15} />
            <input
                id={`edudoc-${name}`}
                className={styles.srchInput}
                type="text"
                value={filters[name]}
                placeholder={placeholder}
                maxLength={SEARCH_MAXLENGTH[name]}
                onChange={e => handleTextChange(name, e.target.value)}
                onKeyDown={handleTextKeyDown}
            />
        </div>
    );

    const fromRecord = totalCount ? page * itemsOnPage + 1 : 0;
    const toRecord = Math.min((page + 1) * itemsOnPage, totalCount);
    const currentPage = page + 1;

    if (!readRight) return <HaventPermission />;

    return (
        <div className={styles.page}>
            <div className={styles.head}>
                <div className={styles.headMain}>
                    <h1 className={styles.title}>Документы об образовании</h1>
                    <p className={styles.subtitle}>
                        Сведения о выданных дипломах, зачётных книжках и академических справках
                    </p>
                </div>
                <div className={styles.headRight}>
                    <button
                        className={styles.resetBtn}
                        onClick={handleResetFilters}
                        disabled={!isFiltersDirty}
                    >
                        Сбросить фильтры
                        <FaRegTrashAlt size={16} />
                    </button>
                </div>
            </div>

            <div className={styles.filters}>
                {renderSelect('Категория получателя', 'cat', catOptions, styles.fCat)}
                {renderSelect('Вид документа', 'type', typeOptions, styles.fType)}
                {renderDate('Выдан с', 'from')}
                {renderDate('по', 'to')}
                {renderSelect('Год выпуска', 'year', yearOptions, styles.fYear)}
                {renderSearch('Поиск по ФИО', 'fio', 'Фамилия получателя')}
                {renderSearch('Поиск по регистрационному номеру', 'number', 'Регистрационный номер')}
            </div>

            {isLoading ? (
                <div className={styles.loader}>
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                </div>
            ) : errorStatus ? (
                <div className={styles.error}>
                    <div className={styles.errorTitle}>Не удалось загрузить перечень</div>
                    {errorStatus === 403
                        ? 'Доступ к режиму запрещён'
                        : errorStatus === 401
                            ? 'Требуется вход в систему'
                            : errorStatus === 0
                                ? 'Нет связи с сервером'
                                : 'Сервер вернул ошибку'}
                    <div>Измените условия отбора или повторите попытку позже.</div>
                </div>
            ) : (
                <>
                    <div className={styles.tableWrap}>
                        <table className={`${styles.table} ${rows.length === 0 ? styles.tableEmpty : ''}`}>
                            <thead>
                                <tr>
                                    {COLUMNS.map(column => (
                                        <th
                                            key={column.key}
                                            className={`${styles.sortable} ${column.col ? styles[column.col] : ''}`}
                                            onClick={() => handleSort(column.key)}
                                        >
                                            {column.title}
                                            <span className={`${styles.caret} ${sort.key === column.key ? styles.caretOn : ''}`}>
                                                {sort.key === column.key ? (sort.dir > 0 ? '▲' : '▼') : '▲'}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {/* пустое состояние занимает область строк внутри рамки таблицы,
                                    а не выводится текстом под ней */}
                                {rows.length === 0 ? (
                                    <tr className={styles.emptyRow}>
                                        <td className={styles.emptyCell} colSpan={COLUMNS.length}>
                                            <div className={styles.emptyTitle}>
                                                {isSelectionSet
                                                    ? 'По заданным условиям документы не найдены'
                                                    : 'Документы об образовании не найдены'}
                                            </div>
                                            {isSelectionSet
                                                ? 'Проверьте период выдачи и остальные условия отбора или нажмите «Сбросить фильтры».'
                                                : 'За период по умолчанию выданных документов нет — измените период выдачи.'}
                                        </td>
                                    </tr>
                                ) : rows.map((row, ind) => (
                                    <tr key={`${row.djid ?? 'row'}-${ind}`} onClick={() => setCurrent(row)}>
                                        {COLUMNS.map(column => (
                                            <td
                                                key={column.key}
                                                className={column.cell ? styles[column.cell] : undefined}
                                            >
                                                {renderValue(row, column)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {rows.length > 0 && (
                        <div className={styles.footrow}>
                            <span className={styles.total}>
                                Найдено записей: <b>{totalCount}</b>
                                <span className={styles.shown}>показаны {fromRecord}–{toRecord}</span>
                            </span>
                            {pagesCount > 1 && (
                                <div className={styles.pager}>
                                    {/* крайние стрелки: на первой странице приглушена «назад»,
                                        на последней — «вперёд» */}
                                    <button
                                        type="button"
                                        className={styles.navBtn}
                                        title="Предыдущая страница"
                                        aria-label="Предыдущая страница"
                                        disabled={currentPage <= 1}
                                        onClick={() => handlePageChange(page - 1)}
                                    >
                                        ‹
                                    </button>
                                    {pagerItems(currentPage, pagesCount).map((item, ind) => item === 'gap'
                                        ? <span key={`gap-${ind}`} className={styles.gap}>…</span>
                                        : <button
                                            key={item}
                                            type="button"
                                            className={item === currentPage ? styles.pageOn : undefined}
                                            onClick={() => handlePageChange(item - 1)}
                                          >
                                            {item}
                                          </button>
                                    )}
                                    <button
                                        type="button"
                                        className={styles.navBtn}
                                        title="Следующая страница"
                                        aria-label="Следующая страница"
                                        disabled={currentPage >= pagesCount}
                                        onClick={() => handlePageChange(page + 1)}
                                    >
                                        ›
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {current && (
                <DocumentCard
                    row={current}
                    onClose={() => setCurrent(null)}
                    filesList={filesList}
                    filesIsLoading={filesIsLoading}
                    filesErrorStatus={filesErrorStatus}
                    getFilesList={getFilesList}
                />
            )}
        </div>
    );
};

export default DocumentList;
